import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Users, Gem, Target, Zap } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { HeroStats } from "@shared/schema";

const fetchHeroStats = async (): Promise<HeroStats> => {
    const res = await fetch("/api/hero-stats");
    if (!res.ok) throw new Error("Failed to fetch hero stats");
    return res.json();
};

const StatItem = ({ icon, value, label, isLoading }: { icon: React.ReactNode, value: string, label: string, isLoading: boolean }) => (
    <motion.div 
        className="text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        {isLoading ? (
            <Skeleton className="h-10 w-10 rounded-full mx-auto mb-3" />
        ) : (
            <div className="h-10 w-10 text-primary mx-auto mb-3">{icon}</div>
        )}
        {isLoading ? (
            <Skeleton className="h-8 w-24 mx-auto mb-2" />
        ) : (
            <div className="text-3xl font-bold">{value}</div>
        )}
        {isLoading ? (
            <Skeleton className="h-4 w-32 mx-auto" />
        ) : (
            <div className="text-sm text-muted-foreground">{label}</div>
        )}
    </motion.div>
);

export default function Stats() {
    const { data: stats, isLoading } = useQuery<HeroStats>({
        queryKey: ["heroStats"],
        queryFn: fetchHeroStats,
    });

    const formattedStats = [
        {
            label: "Total Users",
            value: stats?.activeUsers.toLocaleString() ?? "500+",
            icon: <Users className="w-full h-full" />,
        },
        {
            label: "Total Robux Earned",
            value: stats?.robuxEarned.toLocaleString() ?? "2,000+",
            icon: <Gem className="w-full h-full" />,
        },
        {
            label: "Offers Completed Today",
            value: "1,200+", // This would ideally come from your API
            icon: <Zap className="w-full h-full" />,
        },
        {
            label: "Available Offers",
            value: "4",
            icon: <Target className="w-full h-full" />,
        },
    ];

    return (
        <section className="py-20 bg-background/50">
            <div className="container mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-3xl font-bold tracking-tight">A Thriving Community</h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mt-2">
                        Our platform is buzzing with activity. Here's a snapshot of our community's success.
                    </p>
                </motion.div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {formattedStats.map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ delay: 0.1 * i, duration: 0.5 }}
                        >
                            <StatItem {...stat} isLoading={isLoading} />
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    )
}
